import { CommonModule } from '@angular/common';
import {
  Component,
  computed,
  DestroyRef,
  inject,
  OnInit,
  signal,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormsModule } from '@angular/forms';

import { forkJoin } from 'rxjs';
import {
  ImageUploaderComponent,
  UploadedImage,
} from '@components/app-image-uploader/image-uploader.component';

import { CategoriesService } from '@services/categories/categories.service';
import { Category } from '@services/categories/interfaces/categories.interfaces';
import { ToastService } from '@services/components/ToastService/toast.service';
import { Toast } from '@services/components/ToastService/toast.types';
import {
  AdjustLettersAction,
  KeychainAdminConfig,
  KeychainThickness,
} from '@services/keychains/interfaces/keychains.interfaces';
import { KeychainsService } from '@services/keychains/keychains.service';
import {
  ProductDetail,
  UpdateProductDto,
} from '@services/products/interfaces/product-detail.interfaces';
import { ProductService } from '@services/products/products.service';

import { CDN_PRODUCTS_URL } from '@constants/constants';

interface GeneralKeychainConfig {
  basePrice: number;
  stepPrice: number;
  minLetters: number;
  maxLetters: number;
}

interface KeychainPriceRow {
  letterCount: number;
  prices: number[];
}

interface EditablePrices {
  basePrice: number;
  stepPrice: number;
}

const BASE_SUCCESS_TOAST: Omit<Toast, 'title' | 'description'> = {
  id: '',
  open: true,
  type: 'success',
  variant: 'outline',
  showIcon: true,
  dismissible: true,
};

const BASE_ERROR_TOAST: Omit<Toast, 'title' | 'description'> = {
  id: '',
  open: true,
  type: 'error',
  variant: 'outline',
  showIcon: true,
  dismissible: true,
};

@Component({
  selector: 'app-manage-keychains',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    ImageUploaderComponent,
  ],
  templateUrl: './manage-keychains.html',
  styleUrl: './manage-keychains.css',
})
export class ManageKeychainsComponent implements OnInit {
  public generalConfig = signal<GeneralKeychainConfig | null>(null);
  public thicknesses = signal<KeychainThickness[]>([]);

  public isLoading = signal(false);
  public isUpdatingPrice = signal(false);
  public isAdjustingLetters = signal(false);

  public product = signal<ProductDetail | null>(null);

  public readonly productImages = computed(() => {
    const images = this.product()?.productImages;

    if (!images?.length) {
      return [];
    }

    return [...images]
      .sort((a, b) => a.displayOrder - b.displayOrder)
      .map((img) => {
        const rawUrl = img?.url ?? '';
        const url = !rawUrl
          ? ''
          : /^https?:\/\//.test(rawUrl)
            ? rawUrl
            : `${CDN_PRODUCTS_URL}${rawUrl}`;

        return { url, fileSize: img.fileSize ?? null };
      });
  });
  public mainCategories = signal<Category[]>([]);
  public subCategories = signal<Category[]>([]);
  public isSavingProduct = signal(false);

  public isLetterModalOpen = signal(false);
  public pendingLetterAction = signal<AdjustLettersAction | null>(null);

  public editableBasePrice: number | null = 0;
  public editableStepPrice: number | null = 0;

  public productName = '';
  public productDescription = '';
  public mainCategoryId = '';
  public subCategoryId = '';

  public newImageFiles: File[] = [];
  public removeImageIds: string[] = [];
  public reorderImageIds: string[] = [];

  /**
   * Se incrementa tras guardar para forzar que el uploader se recree y
   * descarte los archivos que aún conserva en memoria, evitando que la
   * imagen recién guardada aparezca duplicada al recargar el producto.
   */
  public readonly uploaderKey = signal(0);

  public readonly minLetters = computed(
    () => this.generalConfig()?.minLetters ?? 0,
  );

  public readonly maxLetters = computed(
    () => this.generalConfig()?.maxLetters ?? 0,
  );

  public readonly priceRows = computed<KeychainPriceRow[]>(() => {
    const config = this.generalConfig();
    const thicknesses = this.thicknesses();

    if (!config) {
      return [];
    }

    const rows: KeychainPriceRow[] = [];

    for (
      let letterCount = config.minLetters;
      letterCount <= config.maxLetters;
      letterCount++
    ) {
      const additionalLetters = letterCount - config.minLetters;

      const prices = thicknesses.map((thickness) => {
        const totalSteps =
          additionalLetters + thickness.stepIndex;

        return config.basePrice + totalSteps * config.stepPrice;
      });

      rows.push({
        letterCount,
        prices,
      });
    }

    return rows;
  });

  private allCategories: Category[] = [];

  private readonly keychainsService = inject(KeychainsService);
  private readonly productService = inject(ProductService);
  private readonly categoriesService = inject(CategoriesService);
  private readonly toastService = inject(ToastService);
  private readonly destroyRef = inject(DestroyRef);

  public ngOnInit(): void {
    this.isLoading.set(true);

    forkJoin({
      adminConfig: this.keychainsService.getAdminConfig(),
      categories: this.categoriesService.getCategories(),
    })
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: ({ adminConfig, categories }) => {
          this.applyConfig(adminConfig);
          this.applyProductSection(adminConfig, categories);
          this.isLoading.set(false);
        },
        error: () => {
          this.isLoading.set(false);

          this.showError(
            'No se pudo cargar la configuración de llaveros.',
          );
        },
      });
  }

  public getImageUrl(rawUrl: string): string {
    return /^https?:\/\//.test(rawUrl)
      ? rawUrl
      : `${CDN_PRODUCTS_URL}${rawUrl}`;
  }

  public onMainCategoryChange(): void {
    this.subCategoryId = '';
    this.refreshSubCategories();
  }

  public saveProduct(): void {
    const product = this.product();

    if (!product || this.isSavingProduct()) {
      return;
    }

    const dto: UpdateProductDto = {
      name: this.productName.trim(),
      description: this.productDescription.trim(),
    };

    if (this.mainCategoryId) {
      dto.mainCategoryId = this.mainCategoryId;
      dto.subCategoryId = this.subCategoryId || null;
    }

    this.isSavingProduct.set(true);

    this.productService
      .updateProduct(product.id, dto)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: () => {
          this.saveProductImages(product.id);
        },
        error: () => {
          this.isSavingProduct.set(false);
          this.showError('No se pudo actualizar el producto.');
        },
      });
  }

  public onImagesUpdated(images: UploadedImage[]): void {
    this.newImageFiles = images
      .map(img => img.file)
      .filter((file): file is File => file !== null);

    const product = this.product();

    if (!product) {
      this.removeImageIds = [];
      this.reorderImageIds = [];

      return;
    }

    const idByUrl = new Map(
      product.productImages.map(image => [this.getImageUrl(image.url), image.id]),
    );

    const remainingExistingIds = images
      .filter(img => img.file === null)
      .map(img => idByUrl.get(img.url))
      .filter((id): id is string => id !== undefined);

    this.removeImageIds = product.productImages
      .filter(original => !remainingExistingIds.includes(original.id))
      .map(original => original.id);

    const originalOrder = [...product.productImages]
      .sort((a, b) => a.displayOrder - b.displayOrder)
      .map(image => image.id);
    const survivingOriginalOrder = originalOrder.filter(id =>
      remainingExistingIds.includes(id),
    );
    const reordered = remainingExistingIds.some(
      (id, index) => survivingOriginalOrder[index] !== id,
    );

    this.reorderImageIds = reordered ? remainingExistingIds : [];
  }

  /**
   * Actualiza únicamente la tabla en pantalla.
   * No guarda los valores en el servidor.
   */
  public applyPricePreview(): void {
    const prices = this.getEditablePrices();

    if (!prices) {
      return;
    }

    this.generalConfig.update((config) => {
      if (!config) {
        return null;
      }

      return {
        ...config,
        basePrice: prices.basePrice,
        stepPrice: prices.stepPrice,
      };
    });
  }

  /**
   * Guarda el precio base y el valor de salto en el servidor.
   */
  public updatePrice(): void {
    if (this.isUpdatingPrice()) {
      return;
    }

    const prices = this.getEditablePrices();

    if (!prices) {
      return;
    }

    /*
     * También actualiza la tabla inmediatamente por si el usuario
     * presionó "Guardar cambios" sin presionar antes el botón ↻.
     */
    this.generalConfig.update((config) => {
      if (!config) {
        return null;
      }

      return {
        ...config,
        basePrice: prices.basePrice,
        stepPrice: prices.stepPrice,
      };
    });

    this.isUpdatingPrice.set(true);

    this.keychainsService
      .updateConfigForAllTypes(prices)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: () => {
          this.isUpdatingPrice.set(false);

          this.showSuccess(
            'Precios actualizados',
            'El precio base y el valor de salto se actualizaron correctamente.',
          );

          this.loadConfig();
        },
        error: () => {
          this.isUpdatingPrice.set(false);
          this.showError('No se pudieron actualizar los precios.');
        },
      });
  }

  /**
   * Abre la modal para confirmar que se agregará o eliminará
   * una cantidad de letras.
   */
  public requestLetterAdjustment(
    action: AdjustLettersAction,
  ): void {
    if (this.isAdjustingLetters()) {
      return;
    }

    if (
      action === 'remove' &&
      this.maxLetters() <= this.minLetters()
    ) {
      this.showError(
        'La cantidad máxima de letras no puede ser menor que la cantidad mínima.',
      );

      return;
    }

    this.pendingLetterAction.set(action);
    this.isLetterModalOpen.set(true);
  }

  public closeLetterModal(): void {
    if (this.isAdjustingLetters()) {
      return;
    }

    this.isLetterModalOpen.set(false);
    this.pendingLetterAction.set(null);
  }

  /**
   * Ejecuta la acción después de la confirmación de la modal.
   */
  public confirmLetterAdjustment(): void {
    const action = this.pendingLetterAction();

    if (!action || this.isAdjustingLetters()) {
      return;
    }

    this.isAdjustingLetters.set(true);

    this.keychainsService
      .adjustLettersForAllTypes(action)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: () => {
          this.isAdjustingLetters.set(false);
          this.isLetterModalOpen.set(false);
          this.pendingLetterAction.set(null);

          const description =
            action === 'add'
              ? 'Se aumentó la cantidad máxima de letras.'
              : 'Se redujo la cantidad máxima de letras.';

          this.showSuccess(
            'Letras actualizadas',
            description,
          );

          this.loadConfig();
        },
        error: () => {
          this.isAdjustingLetters.set(false);

          this.showError(
            'No se pudo modificar la cantidad máxima de letras.',
          );
        },
      });
  }

  private saveProductImages(productId: string): void {
    const hasNewImages = this.newImageFiles.length > 0;
    const hasRemovals = this.removeImageIds.length > 0;
    const hasReorder = this.reorderImageIds.length > 0;

    if (!hasNewImages && !hasRemovals && !hasReorder) {
      this.finishProductSave();
      return;
    }

    const formData = new FormData();

    this.newImageFiles.forEach((file) => {
      formData.append('images', file);
    });

    if (hasRemovals) {
      formData.append(
        'removeImageIds',
        JSON.stringify(this.removeImageIds),
      );
    }

    if (hasReorder) {
      formData.append(
        'reorderImageIds',
        JSON.stringify(this.reorderImageIds),
      );
    }

    this.productService
      .updateProductImages(productId, formData)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: () => {
          this.finishProductSave();
        },
        error: () => {
          this.isSavingProduct.set(false);

          this.showError(
            'El producto se actualizó, pero no se pudieron actualizar las imágenes.',
          );
        },
      });
  }

  private finishProductSave(): void {
    this.isSavingProduct.set(false);
    this.newImageFiles = [];
    this.removeImageIds = [];
    this.reorderImageIds = [];
    this.uploaderKey.update((key) => key + 1);

    this.showSuccess(
      'Producto actualizado',
      'Los cambios se guardaron correctamente.',
    );

    this.loadProductSection();
  }

  private loadProductSection(): void {
    forkJoin({
      adminConfig: this.keychainsService.getAdminConfig(),
      categories: this.categoriesService.getCategories(),
    })
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: ({ adminConfig, categories }) => {
          this.applyProductSection(adminConfig, categories);
        },
        error: () => {
          this.showError(
            'No se pudo cargar el producto del llavero.',
          );
        },
      });
  }

  private applyProductSection(
    adminConfig: KeychainAdminConfig,
    categories: Category[],
  ): void {
    this.allCategories = categories;

    this.mainCategories.set(
      categories.filter((category) => !category.parentId),
    );

    if (!adminConfig.productSlug) {
      this.product.set(null);

      this.showError(
        'No se encontró el producto asociado a los llaveros.',
      );

      return;
    }

    this.loadProductDetail(adminConfig.productSlug);
  }

  private loadProductDetail(slug: string): void {
    this.productService
      .getProductBySlugAdmin(slug)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (product) => {
          if (!product) {
            this.product.set(null);

            this.showError(
              'No se encontró el producto del llavero.',
            );

            return;
          }

          this.product.set(product);

          this.productName = product.name;
          this.productDescription = product.description ?? '';
          this.mainCategoryId = product.mainCategory ?? '';
          this.subCategoryId = product.subCategory ?? '';

          this.refreshSubCategories();
        },
        error: () => {
          this.showError(
            'No se pudo cargar el producto del llavero.',
          );
        },
      });
  }

  private refreshSubCategories(): void {
    const subCategories = this.allCategories.filter(
      (category) =>
        category.parentId === this.mainCategoryId,
    );

    this.subCategories.set(subCategories);

    const selectedSubCategoryExists = subCategories.some(
      (category) => category.id === this.subCategoryId,
    );

    if (!selectedSubCategoryExists) {
      this.subCategoryId = '';
    }
  }

  private loadConfig(): void {
    this.isLoading.set(true);

    this.keychainsService
      .getAdminConfig()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (data) => {
          this.applyConfig(data);
          this.isLoading.set(false);
        },
        error: () => {
          this.isLoading.set(false);

          this.showError(
            'No se pudo cargar la configuración de llaveros.',
          );
        },
      });
  }

  private applyConfig(data: KeychainAdminConfig): void {
    this.thicknesses.set(data.thicknesses);

    const firstConfig = data.config[0] ?? null;

    if (!firstConfig) {
      this.generalConfig.set(null);
      this.editableBasePrice = 0;
      this.editableStepPrice = 0;

      return;
    }

    this.generalConfig.set({
      basePrice: firstConfig.basePrice,
      stepPrice: firstConfig.stepPrice,
      minLetters: firstConfig.minLetters,
      maxLetters: firstConfig.maxLetters,
    });

    this.editableBasePrice = firstConfig.basePrice;
    this.editableStepPrice = firstConfig.stepPrice;
  }

  private getEditablePrices(): EditablePrices | null {
    if (
      this.editableBasePrice === null ||
      this.editableStepPrice === null
    ) {
      this.showError(
        'Debes ingresar el precio base y el valor de salto.',
      );

      return null;
    }

    const basePrice = Number(this.editableBasePrice);
    const stepPrice = Number(this.editableStepPrice);

    const hasInvalidValues =
      !Number.isFinite(basePrice) ||
      !Number.isFinite(stepPrice) ||
      basePrice < 0 ||
      stepPrice < 0;

    if (hasInvalidValues) {
      this.showError(
        'El precio base y el valor de salto deben ser números iguales o mayores que cero.',
      );

      return null;
    }

    return {
      basePrice,
      stepPrice,
    };
  }

  private showSuccess(
    title: string,
    description: string,
  ): void {
    this.toastService.open({
      ...BASE_SUCCESS_TOAST,
      title,
      description,
    });
  }

  private showError(description: string): void {
    this.toastService.open({
      ...BASE_ERROR_TOAST,
      title: 'Error',
      description,
    });
  }
}
